import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  ListItem,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from '@material-ui/core';
import UpdateIcon from '@material-ui/icons/Update';
import { makeStyles } from '@material-ui/styles';
import formatters from 'src/utils/formatters';

const useStyles = makeStyles((theme) => ({
  dispatchUpdateSection: {
    display: 'flex',
    height: '40rem',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  dispatchUpdateTitle: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    width: '100%',
    textAlign: 'center',
  },
  dispatchUpdateList: {
    width: '100%',
    overflowX: 'hidden'
  },
  dispatchUpdateListItem: {
    padding: theme.spacing(2),
    width: '100%',
    margin: theme.spacing(0, 1),
  },
  boldText: {
    fontWeight: theme.typography.fontWeightBold
  },
  cardSection: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    padding: theme.spacing(3.5)
  },
  cardSectionHeader: {
    textAlign: 'center',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  card: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cardHeader: {
    alignSelf: 'center'
  }
}));

const NO_VALUE_DEFAULT = 'N/A';
const STANDARD_DATE_FORMAT = 'PPpp';

function DispatchStatusList({ dispatchStatusUpdates, emptyListMarkup }) {
  const classes = useStyles();

  if (!dispatchStatusUpdates?.length) {
    return emptyListMarkup ?? null;
  }

  return (
    dispatchStatusUpdates.map((statusUpdate) => (
      <ListItem key={statusUpdate.id} alignItems="flex-start" divider className={classes.dispatchUpdateListItem}>
        <Card className={classes.card} raised>
          <CardHeader
            title={(
              <>
                <Typography variant="h3">
                  Status:
                  {' '}
                  {statusUpdate.status}
                </Typography>
                <Typography variant="subtitle2">
                  Status Time:
                  {' '}
                  {formatters.formatISODate(statusUpdate.updateTimeStamp || statusUpdate.dateModified, STANDARD_DATE_FORMAT)}
                </Typography>
              </>
            )}
            avatar={<UpdateIcon fontSize="large" />}
            className={classes.cardHeader}
          />
          <CardContent>
            {statusUpdate?.assignedDriver ? (
              <div className={classes.cardSection}>
                <Typography variant="h5" className={classes.cardSectionHeader}>
                  Assigned Driver
                </Typography>
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Driver Name:
                  </span>
                  {' '}
                  {statusUpdate?.assignedDriver?.name ?? NO_VALUE_DEFAULT}
                </Typography>
                <Typography variant="body2" component="span" />
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Driver ID:
                  </span>
                  {' '}
                  {statusUpdate?.assignedDriver?.id ?? NO_VALUE_DEFAULT}
                </Typography>
              </div>
            ) : null}
            <Divider variant="middle" />
            {statusUpdate?.assignedVehicle ? (
              <div className={classes.cardSection}>
                <Typography variant="h5" className={classes.cardSectionHeader}>
                  Assigned Vehicle
                </Typography>
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Vehicle Name:
                  </span>
                  {' '}
                  {statusUpdate?.assignedVehicle?.name ?? NO_VALUE_DEFAULT}
                </Typography>
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Vehicle ID:
                  </span>
                  {' '}
                  {statusUpdate?.assignedVehicle?.id ?? NO_VALUE_DEFAULT}
                </Typography>
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Vehicle Location:
                  </span>
                  {' '}
                  {`Latitude: ${statusUpdate?.assignedVehicle?.geoPoint.latitude ?? NO_VALUE_DEFAULT}, Longitude: ${statusUpdate?.assignedVehicle?.geoPoint.longitude ?? NO_VALUE_DEFAULT}`}
                </Typography>
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Vehicle Location Last Update:
                  </span>
                  {' '}
                  {statusUpdate?.assignedVehicle?.gpsLastUpdate ? formatters
                    .formatISODate(
                      statusUpdate.assignedVehicle.gpsLastUpdate,
                      STANDARD_DATE_FORMAT
                    ) : NO_VALUE_DEFAULT}
                </Typography>
              </div>
            ) : null}
            <Divider variant="middle" />
            <div className={classes.cardSection}>
              <Typography variant="h5" className={classes.cardSectionHeader}>
                Details
              </Typography>
              {statusUpdate?.eta ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    ETA:
                  </span>
                  {' '}
                  {`${statusUpdate.eta} minutes`}
                </Typography>
              ) : null}
              {statusUpdate?.clearCode ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Clear Code:
                  </span>
                  {' '}
                  {statusUpdate.clearCode}
                </Typography>
              ) : null}
              {statusUpdate?.comments ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Comments:
                  </span>
                  {' '}
                  {statusUpdate.comments}
                </Typography>
              ) : null}

              {statusUpdate?.jobId ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Tow Magic Job ID:
                  </span>
                  {' '}
                  {statusUpdate.jobId}
                </Typography>
              ) : null}
              {statusUpdate?.purchaseOrder ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Purchase Order ID:
                  </span>
                  {' '}
                  {statusUpdate.purchaseOrder}
                </Typography>
              ) : null}
              {statusUpdate?.providerId ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Provider ID:
                  </span>
                  {' '}
                  {statusUpdate.providerId}
                </Typography>
              ) : null}
              {statusUpdate?.locationId ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Location ID:
                  </span>
                  {' '}
                  {statusUpdate.locationId}
                </Typography>
              ) : null}
              {statusUpdate?.zip ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Destination Zip Code:
                  </span>
                  {' '}
                  {statusUpdate.zip}
                </Typography>
              ) : null}
              {statusUpdate?.phone ? (
                <Typography variant="body2">
                  <span className={classes.boldText}>
                    Contact Phone Number:
                  </span>
                  {' '}
                  {statusUpdate.phone}
                </Typography>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </ListItem>
    ))
  );
}

DispatchStatusList.propTypes = {
  dispatchStatusUpdates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    bsCompanyId: PropTypes.number,
    clearCode: PropTypes.string,
    comments: PropTypes.string,
    providerId: PropTypes.string,
    eta: PropTypes.string,
    jobId: PropTypes.string,
    locationId: PropTypes.string,
    motorClubKey: PropTypes.string,
    phone: PropTypes.string,
    purchaseOrderId: PropTypes.string,
    dispatchId: PropTypes.string,
    responseId: PropTypes.string,
    sourceType: PropTypes.string,
    status: PropTypes.string,
    updateTimeStamp: PropTypes.string,
    qoreDispatchStatusId: PropTypes.number,
    zip: PropTypes.string,
    dateCreated: PropTypes.string,
    dateModified: PropTypes.string,
    assignedVehicle: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      geoPoint: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number
      }),
      gpsLastUpdate: PropTypes.string,
      direction: PropTypes.number
    }),
    assignedDriver: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string
    })
  })),
  emptyListMarkup: PropTypes.elementType,
};

export default DispatchStatusList;
